import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RoleName } from '@/components/RoleName'
import { Navigate, useSearchParams } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { CountryText } from '@/components/ui/CountryText'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { StatusPill } from '@/components/ui/StatusPill'
import { TabNav } from '@/components/ui/TabNav'
import { TabPanel } from '@/components/ui/TabPanel'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import {
  useCreateParticipant,
  useCreateTransportRef,
  useDeleteParticipant,
  useDeleteTransportRef,
  useMergeParticipants,
  useParticipants,
  useSetParticipantActive,
  useSetTransportRefActive,
  useTransportRefs,
  useUpdateParticipant,
  useUpdateTransportRef,
} from '@/features/directories/api/useDirectory'
import {
  directoryErrorMessage,
  referencedMessage,
  referencedRefusal,
} from '@/features/directories/directoryText'
import { MergeSheet } from '@/features/directories/components/MergeSheet'
import {
  useCreateUser,
  useResetUserPassword,
  useSetUserActive,
  useUpdateUser,
  useUsers,
} from '@/features/directories/api/useUsers'
import { GeneratedCredentialDialog } from '@/features/directories/components/GeneratedCredentialDialog'
import {
  GRANT_KEYS,
  ParticipantFormSheet,
  TransportFormSheet,
  UserFormSheet,
  type ParticipantDraft,
  type TransportDraft,
  type UserDraft,
} from '@/features/directories/components/DirectoryFormSheets'
import { useSession } from '@/features/auth/api/useSession'
import type {
  CreatedUser,
  CurrentUser,
  DirectoryParticipant,
  DirectoryUser,
  ParticipantRole,
  TransportRef,
} from '@/types/domain'

/* Editing state, shared by all three panels.
 *
 * `null` means the sheet is closed. A record means edit; `'new'` means add. One
 * union rather than an `open` boolean beside a `record`, because those two can
 * disagree — an open sheet with no record and no add intent has no title to
 * render, and that state should not be representable.
 */
type EditTarget<T> = T | 'new' | null

/** The record itself, or undefined when adding. What the sheets take. */
function editing<T>(target: EditTarget<T>): T | undefined {
  return target === null || target === 'new' ? undefined : target
}

/** A row that opens its own editor. Whole rows are clickable, so the target is
 *  the full 38px height rather than a 14px pencil the mouse has to find. */
const EDITABLE_ROW = 'hover:bg-surface-hover cursor-pointer transition-colors'

/* Directories: the records every deal joins to. Today the business joins them
 * by free-text company name, which is the root of "nobody can answer who owes
 * whom without a phone call" — so these three tables are what replace that.
 * Clients have a section of their own since the clients page. */

const TABS = [
  { key: 'participants', labelKey: 'directories.tabs.participants' },
  { key: 'users', labelKey: 'directories.tabs.users' },
  { key: 'transport', labelKey: 'directories.tabs.transport' },
] as const

type TabKey = (typeof TABS)[number]['key']

const HEAD = 'asa-eyebrow h-9 px-3 first:pl-[18px] last:pr-[18px]'
const CELL = 'px-3 first:pl-[18px] last:pr-[18px] text-[13px]'
const ROW = 'border-line-hair h-[38px] border-b last:border-b-0'

function ParticipantsPanel() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const { data, isPending, isError, error, refetch } = useParticipants()
  const [target, setTarget] = useState<EditTarget<DirectoryParticipant>>(null)

  const create = useCreateParticipant()
  const update = useUpdateParticipant()
  const remove = useDeleteParticipant()
  const setActive = useSetParticipantActive()
  const merge = useMergeParticipants()

  const [mergeSource, setMergeSource] = useState<DirectoryParticipant | null>(null)
  const [removingParticipant, setRemovingParticipant] =
    useState<DirectoryParticipant | null>(null)

  /* The refusal matters MORE here than for a client, and for a reason invisible
     from this file: all three of a participant's referencing columns are ON
     DELETE SET NULL, so before the server's reference check was filled this
     delete SUCCEEDED and quietly dropped every case's link to the party. The
     refusal is the whole of what now stops that. */
  const refusal = referencedRefusal(remove.error)

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const canManage =
    session?.role === 'super-admin' || Boolean(session?.grants?.manageParticipants)

  /* Which types this caller may create (FR-40). Agent-typed options are not
     offered to Sales at all rather than offered and refused — and the list they
     browse has no agent rows in it either, because the server filters them out
     (FR-29). The two mechanisms agree; neither is the client's decision. */
  const creatableTypes = creatableParticipantTypes(session)

  const record = target !== null && target !== 'new' ? target : undefined

  const submit = (values: ParticipantDraft) => {
    if (record) {
      update.mutate(
        { id: record.id, payload: values },
        { onSuccess: () => setTarget(null) },
      )
      return
    }
    create.mutate(values, { onSuccess: () => setTarget(null) })
  }

  return (
    <Card>
      <CardHeader
        action={
          <Button size="sm" iconLeft={Plus} onClick={() => setTarget('new')}>
            {t('directories.participants.new')}
          </Button>
        }
      >
        <CardTitle>{t('directories.participants.title')}</CardTitle>
      </CardHeader>

      {refusal ? (
        <div className="px-3.5 pb-2">
          <Banner
            tone="attention"
            title={t('directories.clients.delete')}
            action={
              refusal.suggestion === 'merge' && removingParticipant ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setMergeSource(removingParticipant)
                    remove.reset()
                  }}
                >
                  {t('directories.merge.action')}
                </Button>
              ) : undefined
            }
          >
            {referencedMessage(refusal, t)}
          </Banner>
        </div>
      ) : null}

      {[create, update, setActive, merge].map((mutation, index) =>
        mutation.error ? (
          <div key={index} className="px-3.5 pb-2">
            <ErrorState error={mutation.error} onRetry={() => mutation.reset()} />
          </div>
        ) : null,
      )}

      <div className="asa-scroll overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className={HEAD}>{t('common.fields.name')}</th>
              <th scope="col" className={HEAD}>{t('common.fields.type')}</th>
              <th scope="col" className={HEAD}>{t('common.fields.country')}</th>
              <th scope="col" className={HEAD}>{t('common.fields.status')}</th>
              <th scope="col" className={HEAD}>
                <span className="sr-only">{t('common.actions.label')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((participant) => (
              <tr key={participant.id} className={ROW}>
                <td
                  className={cn(CELL, 'text-text-heading', EDITABLE_ROW, 'cursor-pointer')}
                  onClick={() => setTarget(participant)}
                  tabIndex={0}
                  role="button"
                  aria-label={t('directories.editRecord', { name: participant.name })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setTarget(participant)
                    }
                  }}
                >
                  {participant.name}
                </td>
                <td className={CELL}>
                  <Tag>{t(`participantRole.${participant.type}`)}</Tag>
                </td>
                <td className={cn(CELL, 'text-text-body')}>
                  <CountryText
                    value={participant.country}
                    empty={t('common.empty')}
                  />
                </td>
                <td className={CELL}>
                  <StatusPill tone={participant.isActive ? 'positive' : 'neutral'}>
                    {participant.isActive
                      ? t('common.form.active')
                      : t('common.form.inactive')}
                  </StatusPill>
                </td>
                <td className={cn(CELL, 'text-right whitespace-nowrap')}>
                  {canManage ? (
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={setActive.isPending}
                        onClick={() =>
                          setActive.mutate({
                            id: participant.id,
                            isActive: !participant.isActive,
                          })
                        }
                      >
                        {participant.isActive
                          ? t('directories.deactivate')
                          : t('directories.activate')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={() => {
                          setRemovingParticipant(participant)
                          remove.mutate(participant.id)
                        }}
                      >
                        {t('directories.clients.delete')}
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ParticipantFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={record}
        pending={create.isPending || update.isPending}
        creatableTypes={creatableTypes}
        onSubmit={submit}
      />

      {/* The candidates are narrowed to the SAME TYPE, because the server
          refuses a cross-type merge outright — a shipper folded into an agent
          would move a shipment's counterparty into the hidden partition. Offering
          the other types and meeting that refusal would teach the reader nothing
          the list could have told them. */}
      <MergeSheet
        open={mergeSource !== null}
        onClose={() => setMergeSource(null)}
        source={mergeSource ?? undefined}
        candidates={
          mergeSource
            ? data.filter((row) => row.type === mergeSource.type)
            : []
        }
        pending={merge.isPending}
        error={merge.error ? directoryErrorMessage(merge.error, t) : undefined}
        onConfirm={(targetId) => {
          if (!mergeSource) return
          merge.mutate(
            { targetId, sourceId: mergeSource.id },
            {
              onSuccess: () => {
                setMergeSource(null)
                setRemovingParticipant(null)
              },
            },
          )
        }}
      />
    </Card>
  )
}

/** The participant types this caller may create (FR-40).
 *
 * Mirrors the server's rule rather than inventing one: Pricing, Operations and
 * Finance build and work with agent relationships; Sales adds the parties on
 * their own shipments and cannot create an agent at all. A participant grant
 * widens it, which is Story 7.5.
 *
 * This is an AFFORDANCE. The server refuses regardless — the point is not to
 * offer an option that will be refused. */
function creatableParticipantTypes(
  session: CurrentUser | undefined,
): ParticipantRole[] {
  const all: ParticipantRole[] = [
    'shipper',
    'consignee',
    'agent',
    'counter-agent',
    'vehicle-owner',
    'dispatcher',
    'driver',
  ]

  const seesAgents =
    session?.role === 'super-admin' ||
    session?.role === 'pricing' ||
    session?.role === 'operations' ||
    session?.role === 'finance' ||
    Boolean(session?.grants?.manageParticipants)

  return seesAgents
    ? all
    : all.filter((type) => type !== 'agent' && type !== 'counter-agent')
}

function UsersPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useUsers()
  const { data: session } = useSession()
  const [target, setTarget] = useState<EditTarget<DirectoryUser>>(null)

  /* The generated credential, held only between the mutation resolving and the
     Super-admin acknowledging it. Never written to a query cache: the point of
     a value that is readable once is that nothing keeps it. */
  const [credential, setCredential] = useState<CreatedUser | null>(null)

  const create = useCreateUser()
  const update = useUpdateUser()
  const setActive = useSetUserActive()
  const resetPassword = useResetUserPassword()

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const record = editing(target)
  const pending = create.isPending || update.isPending

  /* The server refuses these two for your own account, and says so clearly. The
     client withholds the controls as well, because an action that is always
     going to be refused should not be offered — EXPERIENCE.md prefers absence
     to a disabled control that explains itself only after being pressed. */
  const isSelf = (user: DirectoryUser) => user.id === session?.id

  const submit = (values: UserDraft) => {
    if (record) {
      // Email is absent: the server does not accept a changed address on an
      // amendment, and the form renders it read-only for the same reason.
      update.mutate(
        {
          id: record.id,
          payload: {
            name: values.name,
            role: values.role,
            office: values.office,
            language: values.language,
            grants: values.grants,
          },
        },
        { onSuccess: () => setTarget(null) },
      )
      return
    }

    create.mutate(values, {
      onSuccess: (result) => {
        setTarget(null)
        // Straight into the hand-off: this is the only moment the credential
        // exists outside the Super-admin's clipboard.
        setCredential(result)
      },
    })
  }

  return (
    <Card>
      <CardHeader
        action={
          <Button size="sm" iconLeft={Plus} onClick={() => setTarget('new')}>
            {t('directories.users.new')}
          </Button>
        }
      >
        <CardTitle>{t('directories.users.title')}</CardTitle>
      </CardHeader>

      {/* A mutation that fails must say so where it was triggered. Without this
          a refused deactivation — the last super-admin, or your own account —
          looks like a button that did nothing. */}
      {[create, update, setActive, resetPassword].map((mutation, index) =>
        mutation.error ? (
          <div key={index} className="px-3.5 pb-2">
            <ErrorState error={mutation.error} onRetry={() => mutation.reset()} />
          </div>
        ) : null,
      )}

      <div className="asa-scroll overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className={HEAD}>{t('common.fields.name')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.email')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.role')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.office')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.grants')}</th>
              <th scope="col" className={HEAD}>{t('common.fields.status')}</th>
              <th scope="col" className={HEAD}>
                <span className="sr-only">{t('common.actions.label')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((user) => {
              const grants = GRANT_KEYS.filter((key) => user.grants[key])

              return (
                <tr key={user.id} className={ROW}>
                  <td
                    className={cn(CELL, 'text-text-heading', EDITABLE_ROW, 'cursor-pointer')}
                    onClick={() => setTarget(user)}
                    tabIndex={0}
                    role="button"
                    aria-label={t('directories.editRecord', { name: user.name })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setTarget(user)
                      }
                    }}
                  >
                    {user.name}
                  </td>
                  <td className={cn(CELL, 'text-text-muted text-[12.5px]')}>
                    {user.email}
                  </td>
                  <td className={CELL}>
                    <Tag tone={user.role === 'super-admin' ? 'brand' : 'neutral'}>
                      <RoleName role={user.role} />
                    </Tag>
                  </td>
                  <td className={cn(CELL, 'text-text-body')}>{user.office}</td>
                  {/* Ownership grants are additive booleans on the user record,
                      never new roles (AD-7, FR-44). */}
                  <td className={cn(CELL, 'text-text-muted text-[12.5px]')}>
                    {grants.length > 0
                      ? grants
                          .map((key) => t(`directories.users.grantLabels.${key}`))
                          .join(', ')
                      : '—'}
                  </td>
                  <td className={CELL}>
                    <div className="flex items-center gap-1.5">
                      <StatusPill tone={user.isActive ? 'positive' : 'neutral'}>
                        {user.isActive
                          ? t('common.form.active')
                          : t('directories.users.inactive')}
                      </StatusPill>
                      {/* Says the credential is still the generated one, which
                          is what a founder chasing a colleague needs to know. */}
                      {user.isActive && user.mustChangePassword ? (
                        <Tag>{t('directories.users.awaitingPasswordChange')}</Tag>
                      ) : null}
                    </div>
                  </td>
                  <td className={cn(CELL, 'text-right whitespace-nowrap')}>
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          resetPassword.mutate(user.id, {
                            onSuccess: (result) => setCredential(result),
                          })
                        }
                        disabled={resetPassword.isPending}
                      >
                        {t('directories.users.resetPassword')}
                      </Button>
                      {isSelf(user) ? null : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setActive.mutate({ id: user.id, isActive: !user.isActive })
                          }
                          disabled={setActive.isPending}
                        >
                          {user.isActive
                            ? t('directories.users.deactivate')
                            : t('directories.users.activate')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <UserFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={record}
        pending={pending}
        onSubmit={submit}
      />

      <GeneratedCredentialDialog
        result={credential}
        onClose={() => setCredential(null)}
      />
    </Card>
  )
}

function TransportPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useTransportRefs()
  /* The kind is carried beside the target because "add" has no record to read
     it from: the same sheet serves both lists, and which list its button sat in
     is the only thing that says whether a new row is a mode or an equipment
     type. */
  const [target, setTarget] = useState<EditTarget<TransportRef>>(null)
  const [kind, setKind] = useState<TransportRef['kind']>('mode')

  const create = useCreateTransportRef()
  const update = useUpdateTransportRef()
  const setActive = useSetTransportRefActive()
  const remove = useDeleteTransportRef()

  /* The refusal, which this panel's own comment has described since Epic 7 while
     no control could produce it: `DELETE /directories/transport/:id` was mounted
     and `useDeleteTransportRef` had no consumer, so "a refused delete offers
     deactivation instead" documented a path nobody could take. */
  const refusal = referencedRefusal(remove.error)

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const modes = data.filter((item) => item.kind === 'mode')
  const equipment = data.filter((item) => item.kind === 'equipment')

  return (
    <div className="grid gap-3.5 lg:grid-cols-2">
      {[
        { kind: 'mode' as const, title: t('directories.transport.modes'), rows: modes },
        {
          kind: 'equipment' as const,
          title: t('directories.transport.equipmentTypes'),
          rows: equipment,
        },
      ].map((group) => (
        <Card key={group.kind}>
          <CardHeader
            action={
              <Button
                variant="secondary"
                size="sm"
                iconLeft={Plus}
                onClick={() => {
                  setKind(group.kind)
                  setTarget('new')
                }}
              >
                {t('directories.transport.add')}
              </Button>
            }
          >
            <CardTitle>{group.title}</CardTitle>
          </CardHeader>
          <ul className="flex flex-col px-[18px] pb-3">
            {group.rows.map((row, index) => (
              <li
                key={row.id}
                className={cn(
                  index !== group.rows.length - 1 && 'border-line-hair border-b',
                )}
              >
                {/* The row's actions are SIBLINGS of the row button, not inside
                    it: a button nested in a button is invalid, and the outer
                    one would swallow the click before either fired. */}
                <div className="flex items-center gap-1">
                  {/* A button rather than a clickable li: this row is a list
                      item, not a table row, so the native control carries the
                      semantics and the keyboard behaviour without either being
                      reimplemented. */}
                  <button
                    type="button"
                    onClick={() => {
                      setKind(row.kind)
                      setTarget(row)
                    }}
                    className="hover:bg-surface-hover -mx-2 flex flex-1 cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 text-left transition-colors"
                  >
                    <span className="text-text-heading text-[13px]">{row.label}</span>
                    <span className="text-text-muted font-mono text-[12px]">
                      {row.code}
                    </span>
                    <span className="flex-1" />
                    {/* Deactivation hides an option going FORWARD: the entry
                        vanishes from the chooser on a new case while records that
                        already reference it keep displaying it (FR-43). */}
                    {!row.isActive ? <Tag>{t('common.form.inactive')}</Tag> : null}
                  </button>

                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={setActive.isPending}
                    onClick={() =>
                      setActive.mutate({ id: row.id, isActive: !row.isActive })
                    }
                  >
                    {row.isActive
                      ? t('directories.deactivate')
                      : t('directories.activate')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(row.id)}
                  >
                    {t('directories.transport.delete')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ))}

      {/* A refused delete offers deactivation instead (FR-43, Story 7.4 AC 5),
          and the message names what still references the entry.

          NO merge action beside it, unlike the client and participant panels: a
          transport mode has nothing to merge into, which is why the server sends
          `deactivate` as its suggestion. The deactivate control is already on
          every row, so the message points at something the reader can see. */}
      {refusal ? (
        <div className="lg:col-span-2">
          <Banner tone="attention" title={t('directories.transport.delete')}>
            {referencedMessage(refusal, t)}
          </Banner>
        </div>
      ) : null}

      {[create, update, setActive].map((mutation, index) =>
        mutation.error ? (
          <div key={index} className="lg:col-span-2">
            <ErrorState error={mutation.error} onRetry={() => mutation.reset()} />
          </div>
        ) : null,
      )}

      {/* One sheet for both lists — only one can be open at a time. */}
      <TransportFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={editing(target)}
        kind={kind}
        pending={create.isPending || update.isPending}
        onSubmit={(values: TransportDraft) => {
          const record = editing(target)
          const payload = {
            // The kind is fixed at creation: moving an entry between the two
            // lists would silently change what every case referencing it means.
            kind: record?.kind ?? kind,
            label: values.label,
            code: values.code,
            sortOrder: record?.sortOrder ?? 0,
          }

          if (record) {
            // Activation is its own endpoint, so the two are separate calls
            // rather than one PUT that quietly does both.
            update.mutate({ id: record.id, payload }, {
              onSuccess: () => {
                if (values.active !== record.isActive) {
                  setActive.mutate({ id: record.id, isActive: values.active })
                }
                setTarget(null)
              },
            })
            return
          }
          create.mutate(payload, { onSuccess: () => setTarget(null) })
        }}
      />
    </div>
  )
}

export function DirectoriesPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const requestedClient = searchParams.get('client')

  /* Clients moved to their own section. The two addresses that used to
     reach them here — the tab, and global search's `?client=` — keep
     working as redirects, because both live in bookmarks and chats. */
  if (requestedClient) {
    return <Navigate to={`/clients/${encodeURIComponent(requestedClient)}`} replace />
  }
  if (requested === 'clients') {
    return <Navigate to="/clients" replace />
  }

  const tab: TabKey = TABS.some((t) => t.key === requested)
    ? (requested as TabKey)
    : 'participants'

  return (
    <>
      <PageHeader
        title={t('directories.title')}
        subtitle={t('directories.subtitle')}
      />

      <TabNav
        label={t('directories.sectionsLabel')}
        items={TABS}
        value={tab}
        onChange={(key) => {
          const next = new URLSearchParams(searchParams)
          next.set('tab', key)
          setSearchParams(next, { replace: true })
        }}
      />

      <TabPanel tabKey={tab}>
        {tab === 'participants' ? <ParticipantsPanel /> : null}
        {tab === 'users' ? <UsersPanel /> : null}
        {tab === 'transport' ? <TransportPanel /> : null}
      </TabPanel>
    </>
  )
}
